<script setup lang="ts">
import SmallModel from '@/components/Interface/Modal/ModelSizes/SmallModel.vue'
import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
</script>

<template>
  <SmallModel :title="t('modals.techLegend.title')">
    <div class="px-2 pt-1">
      <StandardCard class="mb-2">
        <h3 class="font-bold mb-2 text-slate-300">{{ t('modals.techLegend.statusTitle') }}</h3>
        <div class="grid grid-cols-1 gap-2">
          <div class="flex items-center">
            <div class="w-6 h-6 bg-blue-600 rounded-md mr-3"></div>
            <span>{{ t('modals.techLegend.currentlyResearching') }}</span>
          </div>
          <div class="flex items-center">
            <div class="w-6 h-6 bg-green-600 rounded-md mr-3"></div>
            <span>{{ t('modals.techLegend.currentTarget') }}</span>
          </div>
          <div class="flex items-center">
            <div class="w-6 h-6 bg-yellow-600 rounded-md mr-3"></div>
            <span>{{ t('modals.techLegend.requisite') }}</span>
          </div>
          <div class="flex items-center">
            <div class="w-6 h-6 bg-slate-300 rounded-md mr-3"></div>
            <span>{{ t('modals.techLegend.queued') }}</span>
          </div>
        </div>
      </StandardCard>

      <StandardCard class="mb-2">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div class="flex items-center">
            <div class="w-6 h-6 flex items-center justify-center bg-slate-700 rounded-md mr-3">
              <span class="text-yellow-400"><GenericIcon type="energy" /></span>
            </div>
            <span>{{ t('modals.techLegend.icons.planetEnergy') }}</span>
          </div>
          <div class="flex items-center">
            <div class="w-6 h-6 flex items-center justify-center bg-slate-700 rounded-md mr-3">
              <span class="text-red-400"><GenericIcon type="thrust" /></span>
            </div>
            <span>{{ t('modals.techLegend.icons.shipThrust') }}</span>
          </div>

          <div class="flex items-center">
            <div class="w-6 h-6 flex items-center justify-center bg-slate-700 rounded-md mr-3">
              <span class="text-blue-400"><GenericIcon type="production" /></span>
            </div>
            <span>{{ t('modals.techLegend.icons.planetProduction') }}</span>
          </div>
          <div class="flex items-center">
            <div class="w-6 h-6 flex items-center justify-center bg-slate-700 rounded-md mr-3">
              <span class="text-red-400"><GenericIcon type="max_speed" /></span>
            </div>
            <span>{{ t('modals.techLegend.icons.shipMaxSpeed') }}</span>
          </div>

          <div class="flex items-center">
            <div class="w-6 h-6 flex items-center justify-center bg-slate-700 rounded-md mr-3">
              <span class="text-red-400"><GenericIcon type="science" /></span>
            </div>
            <span>{{ t('modals.techLegend.icons.planetScience') }}</span>
          </div>
          <div class="flex items-center">
            <div class="w-6 h-6 flex items-center justify-center bg-slate-700 rounded-md mr-3">
              <span class="text-red-400"><GenericIcon type="scanning_range" /></span>
            </div>
            <span>{{ t('modals.techLegend.icons.scanRange') }}</span>
          </div>

          <div class="flex items-center">
            <div class="w-6 h-6 flex items-center justify-center bg-slate-700 rounded-md mr-3">
              <span class="text-yellow-400"><GenericIcon type="energy_storage" /></span>
            </div>
            <span>{{ t('modals.techLegend.icons.energyStorage') }}</span>
          </div>
          <div class="flex items-center">
            <div class="w-6 h-6 flex items-center justify-center bg-slate-700 rounded-md mr-3">
              <span class="text-blue-300"><GenericIcon type="shields" /></span>
            </div>
            <span>{{ t('modals.techLegend.icons.shields') }}</span>
          </div>

          <div class="flex items-center">
            <div class="w-6 h-6 flex items-center justify-center bg-slate-700 rounded-md mr-3">
              <span class="text-green-400"><GenericIcon type="food" /></span>
            </div>
            <span>{{ t('modals.techLegend.icons.planetFood') }}</span>
          </div>
          <div class="flex items-center">
            <div class="w-6 h-6 flex items-center justify-center bg-slate-700 rounded-md mr-3">
              <span class="text-red-500"><GenericIcon type="hit_points" /></span>
            </div>
            <span>{{ t('modals.techLegend.icons.shipHitPoints') }}</span>
          </div>

          <div class="flex items-center">
            <div class="w-6 h-6 flex items-center justify-center bg-slate-700 rounded-md mr-3">
              <span class="text-orange-400"><GenericIcon type="ore_storage" /></span>
            </div>
            <span>{{ t('modals.techLegend.icons.oreStorage') }}</span>
          </div>
          <div class="flex items-center">
            <div class="w-6 h-6 flex items-center justify-center bg-slate-700 rounded-md mr-3">
              <span class="text-slate-400"><GenericIcon type="frame" /></span>
            </div>
            <span>{{ t('modals.techLegend.icons.shipFrame') }}</span>
          </div>

          <div class="flex items-center">
            <div class="w-6 h-6 flex items-center justify-center bg-slate-700 rounded-md mr-3">
              <span class="text-orange-500"><GenericIcon type="ore" /></span>
            </div>
            <span>{{ t('modals.techLegend.icons.oreExcavation') }}</span>
          </div>
          <div class="flex items-center">
            <div class="w-6 h-6 flex items-center justify-center bg-slate-700 rounded-md mr-3">
              <span class="text-red-400"><GenericIcon type="attack_against_planets" /></span>
            </div>
            <span>{{ t('modals.techLegend.icons.vsPlanetWeapons') }}</span>
          </div>

          <div class="flex items-center">
            <div class="w-6 h-6 flex items-center justify-center bg-slate-700 rounded-md mr-3">
              <span class="text-yellow-300"><GenericIcon type="credit" /></span>
            </div>
            <span>{{ t('modals.techLegend.icons.planetCredits') }}</span>
          </div>
          <div class="flex items-center">
            <div class="w-6 h-6 flex items-center justify-center bg-slate-700 rounded-md mr-3">
              <span class="text-blue-400"><GenericIcon type="attack_against_ships" /></span>
            </div>
            <span>{{ t('modals.techLegend.icons.vsShipWeapons') }}</span>
          </div>

          <div class="flex items-center">
            <div class="w-6 h-6 flex items-center justify-center bg-slate-700 rounded-md mr-3">
              <span class="text-orange-300"><GenericIcon type="refinery" /></span>
            </div>
            <span>{{ t('modals.techLegend.icons.oreRefinery') }}</span>
          </div>
        </div>
      </StandardCard>
    </div>
  </SmallModel>
</template>
